<script setup lang="ts">
import { CircleCheck, Info, OctagonX, TriangleAlert } from 'lucide';
import { computed, onMounted, ref, useSlots } from 'vue';
import { SphAutoCollapse } from '@/components/base/_internal/auto-collapse';
import { SphIcon } from '@/components/base/icon';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = withDefaults(defineProps<{
    type?: 'info' | 'success' | 'warning' | 'error'
    title?: string
    description?: string
    animated?: boolean
}>(), {
    type: 'info',
    title: '',
    description: '',
    animated: false,
});

const typeIcon = computed(() => {
    switch (props.type) {
        default:
        case 'info':
            return Info;
        case 'success':
            return CircleCheck;
        case 'warning':
            return TriangleAlert;
        case 'error':
            return OctagonX;
    }
});

const slots = useSlots();

const showContent = ref(!props.animated);
onMounted(() => {
    showContent.value = true;
});
</script>

<template>
    <div
        class="sph-callout"
        :class="`type-${props.type}`"
        role="alert"
    >
        <SphAutoCollapse
            :model-value="showContent"
        >
            <div class="notice">
                <div class="icon">
                    <slot name="icon">
                        <SphIcon
                            :icon="typeIcon"
                            size="xl"
                        />
                    </slot>
                </div>

                <div class="content">
                    <h3 class="title">
                        <slot name="title">
                            {{ title }}
                        </slot>
                    </h3>
                    <p
                        v-if="description || slots.description"
                        class="description"
                    >
                        <slot name="description">
                            {{ description }}
                        </slot>
                    </p>

                    <template
                        v-if="slots.actions"
                    >
                        <div class="actions">
                            <slot name="actions"></slot>
                        </div>
                    </template>
                </div>
            </div>
        </SphAutoCollapse>
    </div>
</template>

<style>
@reference '@/assets/styles/config.css';

.sph-callout {
    .notice {
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        border-radius: var(--radius-lg);
        border: 1px solid var(--callout-border-color, var(--color-info-border));
        background-color: var(--callout-bg-color, color-mix(in srgb, var(--color-info) 50%, transparent));
        color: var(--callout-text-color, var(--color-info-foreground));
    }

    &.type-info .notice {
        --callout-border-color: var(--color-slate-400);
        --callout-bg-color: color-mix(in srgb, var(--color-info) 15%, transparent);
        --callout-text-color: var(--color-info-foreground);
    }

    &.type-success .notice {
        --callout-border-color: var(--color-success-border);
        --callout-bg-color: color-mix(in srgb, var(--color-success) 15%, transparent);
        --callout-text-color: var(--color-success-foreground);
    }

    &.type-warning .notice {
        --callout-border-color: var(--color-warning-border);
        --callout-bg-color: color-mix(in srgb, var(--color-warning) 15%, transparent);
        --callout-text-color: var(--color-warning-foreground);
    }

    &.type-error .notice {
        --callout-border-color: var(--color-error-border);
        --callout-bg-color: color-mix(in srgb, var(--color-error) 15%, transparent);
        --callout-text-color: var(--color-error-foreground);
    }

    .icon {
        color: var(--callout-border-color);

        .sph-icon {
            display: block;
        }
    }

    .content {
        flex: 1 1 auto;
        min-width: 0;
    }

    .title {
        font-size: var(--text-base);
        font-weight: var(--font-weight-semibold);
    }

    .description {
        margin-top: 0.25rem;
        font-size: var(--text-sm);
        font-weight: var(--font-weight-normal);
        line-height: var(--line-height-relaxed);
    }

    .actions {
        margin-top: 1rem;
        display: flex;
        flex-flow: row nowrap;
        gap: 1rem;

        button {
            box-shadow: none;
            border-color: transparent;
            background-color: color-mix(in srgb, var(--callout-border-color) 50%, transparent);
            color: var(--callout-text-color);

            &:hover {
                border-color: transparent;
                background-color: color-mix(in srgb, var(--callout-bg-color) 60%, var(--callout-text-color));
            }
        }
    }
}
</style>
